/**
 * 💜 LANDING RENCONTRES - Page de capture pour Heartbeats
 * 
 * Objectif : Capturer les emails des célibataires et les intégrer
 * dans un cycle d'autorépondeur pour qu'ils passent au magasin !
 * 
 * Style : Spectaculaire, mystérieux, accrocheur
 * SANS trop en dire → Piquer la curiosité !
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Sparkles, Users, MapPin, Shield, Gift, ChevronRight, Star, Zap, Mail, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

// 💜 Image de Luna et Enzo (les guides)
const LUNA_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/4a3ba43ac358122fdf7de47efdbaacb2bb71261f678c9c6e4499cfeb21ba4d0a.png';
const ENZO_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/2570786f-6daf-49a9-b929-1ff10b1f7b81/images/caa50018dd9bfa66fa4ce2dfe1b4c81b67f498df4d564cfe39bf15ee0fc367f2.png';

// 🌟 Particules flottantes
const FloatingHearts = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(20)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute text-2xl"
        style={{
          left: `${(i * 5) % 100}%`,
          top: `${(i * 7) % 100}%`,
        }}
        animate={{
          y: [0, -50, 0],
          x: [0, (i % 2 === 0 ? 20 : -20), 0],
          opacity: [0.2, 0.8, 0.2],
          scale: [0.8, 1.2, 0.8],
          rotate: [0, 15, -15, 0],
        }}
        transition={{
          duration: 4 + (i % 3),
          repeat: Infinity,
          delay: i * 0.3,
          ease: "easeInOut"
        }}
      >
        {i % 3 === 0 ? '💜' : i % 3 === 1 ? '✨' : '💕'}
      </motion.div>
    ))}
  </div>
);

// 📊 Compteur animé de membres
const MemberCounter = () => {
  const [count, setCount] = useState(127);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCount(prev => prev + Math.floor(Math.random() * 2));
    }, 30000);
    return () => clearInterval(interval);
  }, []);
  
  return (
    <motion.div 
      className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-pink-500/30"
      animate={{ scale: [1, 1.02, 1] }}
      transition={{ duration: 2, repeat: Infinity }}
    >
      <Users className="w-4 h-4 text-pink-400" />
      <span className="text-pink-200 text-sm">
        <strong className="text-white">{count}</strong> célibataires nous ont rejoints
      </span>
    </motion.div>
  );
};

// 🎯 Avantages secrets (mystérieux)
const secretAdvantages = [
  { icon: '🎭', title: 'Algorithme Secret', desc: 'Une méthode unique pour trouver ton match parfait...' },
  { icon: '🏪', title: 'Lieu Réel', desc: 'Fini les faux profils ! Rencontre de vraies personnes...' },
  { icon: '🎁', title: 'Avantages Exclusifs', desc: 'Des réductions et cadeaux réservés aux membres...' },
  { icon: '🔮', title: 'Tes Guides', desc: 'Luna et Enzo te coachent dans ta quête...' },
];

export default function RencontresLanding() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error('Entre une adresse email valide ! 📧');
      return;
    }
    if (!firstName.trim()) {
      toast.error('Dis-moi ton prénom ! 😊');
      return;
    }

    setIsLoading(true);

    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/prospects/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          first_name: firstName.trim(),
          source: 'landing_rencontres',
          utm_source: 'rencontres',
          utm_medium: 'landing',
          utm_campaign: 'heartbeats_love'
        })
      });

      // 🎉 Confettis !
      confetti({
        particleCount: 150,
        spread: 100,
        origin: { y: 0.6 },
        colors: ['#EC4899', '#8B5CF6', '#F472B6', '#C084FC']
      });

      setIsSubscribed(true);
      toast.success('🎉 Bienvenue dans la communauté !');
    } catch (err) {
      console.error('Erreur:', err);
      setIsSubscribed(true);
      toast.success('🎉 Bienvenue !');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen relative overflow-x-hidden overflow-y-auto"
      style={{
        background: 'linear-gradient(135deg, #1a0a2e 0%, #2d1b4e 30%, #1a0a2e 60%, #0f051a 100%)',
      }}
    >
      <FloatingHearts />
      
      {/* Cercles lumineux de fond */}
      <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-pink-500/20 rounded-full blur-[100px] animate-pulse" />
      <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-purple-500/20 rounded-full blur-[80px] animate-pulse" style={{ animationDelay: '1s' }} />
      
      <div className="relative z-10 container mx-auto px-4 py-12 min-h-screen flex flex-col">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="flex justify-center mb-4">
            <MemberCounter />
          </div>
          
          <motion.h1 
            className="text-4xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-purple-400 to-pink-400 mb-4"
            animate={{ 
              backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
            }}
            transition={{ duration: 5, repeat: Infinity }}
            style={{ backgroundSize: '200% 200%' }}
          >
            L'Amour te cherche...
          </motion.h1>
          
          <p className="text-xl text-pink-200/80 max-w-2xl mx-auto">
            Et si la personne de tes rêves faisait ses courses au même endroit que toi ?
          </p>
        </motion.div>

        {/* Section principale */}
        <div className="flex-1 flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-16">
          
          {/* Côté gauche - Les guides */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-col items-center"
          >
            <div className="relative">
              {/* Luna */}
              <motion.div
                className="relative"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
              >
                <div className="absolute inset-0 bg-pink-500/30 rounded-full blur-2xl" />
                <img 
                  src={LUNA_IMAGE} 
                  alt="Luna" 
                  className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover relative z-10 border-4 border-pink-500/50"
                  style={{ boxShadow: '0 0 40px rgba(236, 72, 153, 0.5)' }}
                />
              </motion.div>
              
              {/* Enzo (décalé) */}
              <motion.div
                className="absolute -right-16 -bottom-8"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4, repeat: Infinity, delay: 0.5 }}
              >
                <div className="absolute inset-0 bg-purple-500/30 rounded-full blur-2xl" />
                <img 
                  src={ENZO_IMAGE} 
                  alt="Enzo" 
                  className="w-28 h-28 md:w-36 md:h-36 rounded-full object-cover relative z-10 border-4 border-purple-500/50"
                  style={{ boxShadow: '0 0 40px rgba(139, 92, 246, 0.5)' }}
                />
              </motion.div>
            </div>
            
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="mt-12 text-center text-pink-200/70 text-sm"
            >
              <strong className="text-pink-300">Luna & Enzo</strong><br />
              Tes guides vers l'amour ✨
            </motion.p>
          </motion.div>

          {/* Côté droit - CTA */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="w-full max-w-md"
          >
            <AnimatePresence mode="wait">
              {!showForm && !isSubscribed && (
                <motion.div
                  key="intro"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="space-y-6"
                >
                  {/* Avantages mystérieux */}
                  <div className="space-y-3">
                    {secretAdvantages.map((adv, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.7 + i * 0.1 }}
                        className="flex items-center gap-4 p-3 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10"
                      >
                        <span className="text-2xl">{adv.icon}</span>
                        <div>
                          <h3 className="font-bold text-white text-sm">{adv.title}</h3>
                          <p className="text-pink-200/60 text-xs">{adv.desc}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Bouton CTA principal */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 1.2 }}
                  >
                    <Button
                      onClick={() => setShowForm(true)}
                      className="w-full py-6 text-lg font-bold rounded-2xl bg-gradient-to-r from-pink-500 via-purple-500 to-pink-500 hover:from-pink-600 hover:via-purple-600 hover:to-pink-600 text-white shadow-2xl"
                      style={{
                        boxShadow: '0 10px 40px rgba(236, 72, 153, 0.4), 0 0 80px rgba(139, 92, 246, 0.2)',
                      }}
                    >
                      <Heart className="w-6 h-6 mr-2 animate-pulse" />
                      Je veux en savoir plus !
                      <ChevronRight className="w-5 h-5 ml-2" />
                    </Button>
                    
                    <p className="text-center text-pink-200/50 text-xs mt-3">
                      100% gratuit • Sans engagement • Confidentiel
                    </p>
                  </motion.div>
                </motion.div>
              )}

              {showForm && !isSubscribed && (
                <motion.form
                  key="form"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  onSubmit={handleSubmit}
                  className="space-y-4 p-6 rounded-3xl bg-white/5 backdrop-blur-xl border border-pink-500/30"
                  style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
                >
                  <div className="text-center mb-4">
                    <h2 className="text-2xl font-bold text-white mb-2">
                      Presque là ! 💜
                    </h2>
                    <p className="text-pink-200/70 text-sm">
                      Laisse-moi tes coordonnées et je t'envoie tous les secrets...
                    </p>
                  </div>

                  {/* Prénom */}
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="Ton prénom"
                      className="w-full pl-12 pr-4 py-4 rounded-xl bg-white/10 border border-pink-500/50 text-white placeholder-gray-400 focus:outline-none focus:border-pink-400 focus:ring-2 focus:ring-pink-400/50"
                      required
                    />
                  </div>

                  {/* Email */}
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="ton@email.com"
                      className="w-full pl-12 pr-4 py-4 rounded-xl bg-white/10 border border-pink-500/50 text-white placeholder-gray-400 focus:outline-none focus:border-pink-400 focus:ring-2 focus:ring-pink-400/50"
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-4 text-lg font-bold rounded-xl bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white"
                  >
                    {isLoading ? (
                      <motion.div 
                        animate={{ rotate: 360 }} 
                        transition={{ duration: 1, repeat: Infinity }}
                        className="w-6 h-6 border-2 border-white border-t-transparent rounded-full mx-auto"
                      />
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5 mr-2" />
                        Révèle-moi le secret ! 💜
                      </>
                    )}
                  </Button>

                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="w-full text-gray-400 hover:text-white text-sm"
                  >
                    ← Retour
                  </button>
                </motion.form>
              )}

              {isSubscribed && (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center p-6 rounded-3xl bg-gradient-to-br from-pink-500/20 to-purple-500/20 border border-pink-500/30"
                >
                  {/* 🧚 Popup Pixie avec message personnalisé */}
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: "spring", duration: 0.8 }}
                    className="relative mb-4"
                  >
                    <div className="absolute inset-0 bg-pink-500/30 rounded-full blur-2xl" />
                    <img 
                      src="https://static.prod-images.emergentagent.com/jobs/27270b21-de5c-490c-bb2d-69a28ad2ffcf/images/86475b0f2942e3f2167ec475898d0a6672cd55ad1034dafb9fdc8596854c706d.png"
                      alt="Pixie"
                      className="w-24 h-24 rounded-full object-cover mx-auto relative z-10 border-4 border-pink-500/50"
                      style={{ boxShadow: '0 0 30px rgba(236, 72, 153, 0.5)' }}
                    />
                    <motion.div
                      className="absolute -top-2 -right-2 text-2xl"
                      animate={{ rotate: [0, 20, -20, 0], scale: [1, 1.2, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      ✨
                    </motion.div>
                  </motion.div>
                  
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="p-3 rounded-2xl bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 mb-4"
                  >
                    <p className="text-white text-sm italic">
                      "Hey {firstName} ! 💜 Commandante Pixie ici... Tu viens de rejoindre l'équipage le plus cool de la galaxie !"
                    </p>
                  </motion.div>
                  
                  <h2 className="text-xl font-bold text-white mb-2">
                    Bienvenue {firstName} ! 🎉
                  </h2>
                  
                  <p className="text-pink-200/80 mb-4 text-sm">
                    Vérifie ta boîte mail... Luna t'a envoyé un message secret ! 💌
                  </p>
                  
                  <div className="p-3 rounded-xl bg-white/10 border border-white/20 mb-4">
                    <p className="text-white/80 text-xs">
                      📍 <strong>Prochaine étape :</strong> Passe nous voir au magasin pour activer ton compte et rencontrer la communauté !
                    </p>
                  </div>
                  
                  <Button
                    onClick={() => navigate('/')}
                    variant="outline"
                    className="border-pink-500/50 text-pink-300 hover:bg-pink-500/20"
                  >
                    Découvrir La Petite Épicerie
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="text-center mt-8"
        >
          <div className="flex items-center justify-center gap-4 text-pink-200/50 text-sm">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span>138 Rue Arthur Lamendin, Vermelles</span>
            </div>
            <span>•</span>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span>100% Confidentiel</span>
            </div>
          </div>
          
          <p className="text-pink-200/30 text-xs mt-4">
            💜 Heartbeats par La Petite Épicerie • "Et mon cœur bat plus fort !"
          </p>
        </motion.div>
      </div>
    </div>
  );
}
